import { useMemo } from 'react';

import type { ProductId } from '../../../services/modal-data/types';
import type { ModalState } from './types';

/** All TWC-eligible products, in canonical render order. */
export const TWC_ELIGIBLE_PRODUCTS: readonly ProductId[] = ['jira', 'confluence', 'loom', 'rovo'];

export type TwcProductSplit = {
	/** TWC-eligible products the user already has, in canonical order. */
	existingProducts: ProductId[];
	/** TWC-eligible products that are new to the user, in canonical order. */
	newProducts: ProductId[];
};

/**
 * Splits the TWC-eligible products into the ones the user already has and the ones that are new to
 * them.
 *
 * `d0ActiveProducts` is the canonical source for the split.
 *
 * Both the confirmation screen and the welcome step derive their split from here. They have to
 * agree: the confirmation screen tells the user which products they are about to get, and the
 * welcome step names the same products moments later in the same flow. Deriving the split twice
 * would let the two drift apart.
 *
 * An empty `d0ActiveProducts` remains meaningful and is not replaced with legacy `userProducts`.
 */
export const useTwcProductSplit = ({ d0ActiveProducts }: ModalState): TwcProductSplit => {
	return useMemo(() => {
		const selected = new Set(d0ActiveProducts);

		return {
			existingProducts: TWC_ELIGIBLE_PRODUCTS.filter((product) => selected.has(product)),
			newProducts: TWC_ELIGIBLE_PRODUCTS.filter((product) => !selected.has(product)),
		};
	}, [d0ActiveProducts]);
};
